/**
 * PaymentsTab Component - Extracted from FinanceDashboard
 * Handles payment recording and recent payments listing
 */
import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CreditCard, RefreshCw, Receipt, Paperclip, Image as ImageIcon, X, History } from "lucide-react";

const initialPaymentForm = {
  invoice_id: '',
  amount: '',
  payment_date: new Date().toISOString().split('T')[0],
  payment_method: 'bank_transfer',
  reference_number: '',
  notes: '',
  receipt_url: '',
  // NEW: Payment type selector + HRDCorp fields
  payment_type: 'self_pay', // 'self_pay' | 'hrdcorp' | 'partial' | 'other'
  hrdcorp_service_fee: '',
  hrdcorp_invoice_number: '',
  hrdcorp_invoice_date: '',
  hrdcorp_invoice_url: '',
  // Upfront advance: payment does NOT fully settle the invoice (balance stays)
  is_partial_payment: false,
  // Legacy CN flow (kept for backward compatibility)
  create_cn: false,
  cn_mode: 'percentage', // 'percentage' or 'amount'
  cn_percentage: '4',
  cn_amount: '',
  cn_reason: 'HRDCorp Levy Deduction'
};

const PaymentsTab = ({
  invoices,
  payments,
  companySettings,
  onRefresh,
  selectedInvoiceId,
  onViewAllPayments,
}) => {
  const [paymentForm, setPaymentForm] = useState({
    ...initialPaymentForm,
    invoice_id: selectedInvoiceId || ''
  });
  // PHASE 3A (Section AD/AE): canonical outstanding for the selected invoice.
  const [canonicalOutstanding, setCanonicalOutstanding] = useState(null);
  const [outstandingError, setOutstandingError] = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  // Filter pending invoices for payment
  const pendingInvoices = invoices.filter(inv => inv.status === 'issued' || inv.status === 'partially_paid');

  // Phase 3A FINAL Section F: use a useRef so a late response for invoice A
  // cannot populate invoice B even during rapid state churn.
  const [outstandingReqToken, setOutstandingReqToken] = useState(0);
  const outstandingReqRef = useRef(0);
  const [savingPayment, setSavingPayment] = useState(false);
  const handleInvoiceSelect = async (invoiceId) => {
    outstandingReqRef.current += 1;
    const token = outstandingReqRef.current;
    setOutstandingReqToken(token);
    setPaymentForm(prev => ({ ...prev, invoice_id: invoiceId, amount: '' }));
    setCanonicalOutstanding(null);
    setOutstandingError(null);
    if (!invoiceId) return;
    setOutstandingLoading(true);
    try {
      const r = await axiosInstance.get(`/finance/source-of-truth/invoice/${invoiceId}/outstanding`);
      // Ignore stale responses.
      if (token !== outstandingReqRef.current) return;
      const c = r.data || {};
      setCanonicalOutstanding(c);
      const defaultAmount = Number(c.outstanding_amount || 0);
      setPaymentForm(prev => (
        prev.invoice_id === invoiceId
          ? { ...prev, amount: defaultAmount > 0 ? defaultAmount : '' }
          : prev
      ));
    } catch (err) {
      if (token !== outstandingReqRef.current) return;
      setOutstandingError('Unable to verify outstanding amount from server. Please retry.');
      setPaymentForm(prev => (
        prev.invoice_id === invoiceId ? { ...prev, amount: '' } : prev
      ));
    } finally {
      if (token === outstandingReqRef.current) {
        setOutstandingLoading(false);
      }
    }
  };

  // Phase 3A Section 19: when selectedInvoiceId prop is (re)set from the
  // parent (e.g. clicking "Record Payment" on an invoice row), auto-run
  // the canonical outstanding fetch so canonicalOutstanding is populated
  // and paymentDisabled evaluates correctly.
  useEffect(() => {
    if (selectedInvoiceId) {
      handleInvoiceSelect(selectedInvoiceId);
    }
  }, [selectedInvoiceId]);

  // Reset canonicalOutstanding + error when the form is cleared.
  const clearForm = () => {
    setPaymentForm(initialPaymentForm);
    setCanonicalOutstanding(null);
    setOutstandingError(null);
  };

  const paymentDisabled = (
    savingPayment ||
    !paymentForm.invoice_id ||
    outstandingLoading ||
    !!outstandingError ||
    !canonicalOutstanding ||
    (canonicalOutstanding && Number(canonicalOutstanding.outstanding_amount || 0) <= 0) ||
    !paymentForm.amount ||
    !Number.isFinite(Number(paymentForm.amount)) ||
    Number(paymentForm.amount) <= 0 ||
    (canonicalOutstanding && Number(paymentForm.amount) > Number(canonicalOutstanding.outstanding_amount || 0) + 0.01)
  );

  // Record payment API call
  const handleRecordPayment = async () => {
    if (paymentDisabled) {
      toast.error(outstandingError || "Select an invoice and enter a valid amount within outstanding.");
      return;
    }
    setSavingPayment(true);
    try {
      const response = await axiosInstance.post('/finance/payments', {
        invoice_id: paymentForm.invoice_id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes,
        receipt_url: paymentForm.receipt_url || null,
        payment_type: paymentForm.payment_type,
        hrdcorp_service_fee: paymentForm.payment_type === 'hrdcorp' ? parseFloat(paymentForm.hrdcorp_service_fee || 0) : null,
        hrdcorp_invoice_number: paymentForm.payment_type === 'hrdcorp' ? paymentForm.hrdcorp_invoice_number : null,
        hrdcorp_invoice_date: paymentForm.payment_type === 'hrdcorp' ? paymentForm.hrdcorp_invoice_date : null,
        hrdcorp_invoice_url: paymentForm.payment_type === 'hrdcorp' ? (paymentForm.hrdcorp_invoice_url || null) : null,
        // CN flow only allowed for non-HRDCorp payments (HRDCorp uses expense account instead)
        create_credit_note: paymentForm.create_cn,
        is_partial_payment: !!paymentForm.is_partial_payment,
        deduction_percentage: paymentForm.create_cn && paymentForm.cn_mode === 'percentage' ? parseFloat(paymentForm.cn_percentage) : null,
        deduction_amount: paymentForm.create_cn && paymentForm.cn_mode === 'amount' ? parseFloat(paymentForm.cn_amount) : null,
        deduction_reason: paymentForm.create_cn ? paymentForm.cn_reason : null
      });
      
      if (response.data.credit_note) {
        toast.success(`Payment recorded & Credit Note ${response.data.credit_note.cn_number} created!`);
      } else {
        toast.success("Payment recorded successfully");
      }
      setPaymentForm(initialPaymentForm);
      setCanonicalOutstanding(null);
      setOutstandingError(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSavingPayment(false);
    }
  };

  // Print receipt
  const handlePrintReceipt = async (payment) => {
    try {
      const { printReceipt } = await import('../../utils/printReceipt');
      printReceipt(payment, companySettings, axiosInstance);
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to generate receipt");
    }
  };

  // Handle proof-of-payment file selection (base64-encode, max 5 MB)
  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error("Only image or PDF files are allowed");
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be 5 MB or smaller");
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPaymentForm((prev) => ({ ...prev, receipt_url: reader.result }));
    };
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsDataURL(file);
  };

  // Handle HRDCorp invoice file selection (same constraints)
  const handleHrdInvoiceFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error("Only image or PDF files are allowed");
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be 5 MB or smaller");
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPaymentForm((prev) => ({ ...prev, hrdcorp_invoice_url: reader.result }));
    };
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsDataURL(file);
  };

  // Open data URL in new tab — handles both images and PDFs correctly
  const openDataUrl = (dataUrl, label = 'Attachment') => {
    if (!dataUrl) return;
    const w = window.open();
    if (!w) { toast.error("Popup blocked — please allow popups"); return; }
    const isPdf = dataUrl.startsWith('data:application/pdf');
    if (isPdf) {
      w.document.write(`<title>${label}</title><body style="margin:0"><iframe src="${dataUrl}" style="border:0;width:100vw;height:100vh"></iframe></body>`);
    } else {
      w.document.write(`<title>${label}</title><body style="margin:0;background:#222;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${dataUrl}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body>`);
    }
  };

  // View an existing payment's proof in a new tab
  const handleViewProof = async (paymentId) => {
    try {
      const { data } = await axiosInstance.get(`/finance/payments/${paymentId}/proof`);
      if (!data?.receipt_url) {
        toast.info("No proof of payment uploaded for this payment");
        return;
      }
      openDataUrl(data.receipt_url, 'Proof of Payment');
    } catch (e) {
      toast.error("Failed to load proof of payment");
    }
  };

  // View the linked HRDCorp invoice
  const handleViewHrdInvoice = async (paymentId) => {
    try {
      const { data } = await axiosInstance.get(`/finance/payments/${paymentId}/hrdcorp-invoice`);
      if (!data?.hrdcorp_invoice_url) {
        toast.info("No HRDCorp invoice uploaded for this payment");
        return;
      }
      openDataUrl(data.hrdcorp_invoice_url, `HRDCorp Invoice ${data.hrdcorp_invoice_number || ''}`);
    } catch (e) {
      toast.error("Failed to load HRDCorp invoice");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Record Payment Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            Record Payment
          </CardTitle>
          <CardDescription>Record payment received for an invoice</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Select Invoice (Pending Only)</Label>
            <Select value={paymentForm.invoice_id} onValueChange={handleInvoiceSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select an invoice to pay" />
              </SelectTrigger>
              <SelectContent>
                {pendingInvoices.length === 0 ? (
                  <SelectItem value="none" disabled>No pending invoices</SelectItem>
                ) : (
                  pendingInvoices.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.invoice_number} - {inv.company_name || inv.session_name} (RM {inv.total_amount?.toLocaleString()})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Payment Type Selector */}
          <div data-testid="payment-type-selector">
            <Label>Payment Type *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
              {[
                { v: 'self_pay', label: 'Self Pay' },
                { v: 'hrdcorp', label: 'HRDCorp Grant' },
                { v: 'partial', label: 'Partial' },
                { v: 'other', label: 'Other' }
              ].map(opt => (
                <Button
                  key={opt.v}
                  type="button"
                  size="sm"
                  variant={paymentForm.payment_type === opt.v ? 'default' : 'outline'}
                  className={paymentForm.payment_type === opt.v ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setPaymentForm({ ...paymentForm, payment_type: opt.v })}
                  data-testid={`payment-type-${opt.v}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            {paymentForm.payment_type === 'hrdcorp' && (
              <p className="text-xs text-blue-700 mt-1">
                HRDCorp service fee will be booked as an expense to <strong>6650 HRDCorp Service Charges</strong>. If HRDCorp only approved a partial amount, tick &ldquo;Create Credit Note&rdquo; below to write off the shortfall.
              </p>
            )}
            {paymentForm.payment_type === 'partial' && (
              <p className="text-xs text-amber-700 mt-1">Invoice will remain partially paid until the balance is received.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label data-testid="payment-amount-label">Payment Amount (RM)</Label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                placeholder="0.00"
              />
              {canonicalOutstanding && (
                <p className="text-xs mt-1" data-testid="canonical-outstanding-hint">
                  <span className="text-slate-500">Invoice Face Value:</span>{' '}
                  <strong>RM {Number(canonicalOutstanding.document_face_value || 0).toLocaleString('en-MY', {minimumFractionDigits: 2})}</strong>
                  {' · '}
                  <span className="text-slate-500">Canonical Outstanding:</span>{' '}
                  <strong className={canonicalOutstanding.outstanding_amount > 0 ? 'text-green-700' : 'text-slate-400'}>
                    RM {Number(canonicalOutstanding.outstanding_amount || 0).toLocaleString('en-MY', {minimumFractionDigits: 2})}
                  </strong>
                  {canonicalOutstanding.outstanding_amount <= 0 && (
                    <span className="text-red-600 ml-2" data-testid="invoice-fully-settled-badge">· Fully settled</span>
                  )}
                </p>
              )}
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date" 
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="online">Online Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input 
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                placeholder="Transaction ref"
              />
            </div>
          </div>
          
          <div>
            <Label>Notes (Optional)</Label>
            <Input 
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
              placeholder="Additional notes"
            />
          </div>

          {/* HRDCorp-specific fields (shown only when payment_type=hrdcorp) */}
          {paymentForm.payment_type === 'hrdcorp' && (() => {
            const selectedInvoice = invoices.find(inv => inv.id === paymentForm.invoice_id);
            const invoiceTotal = selectedInvoice?.total_amount || 0;
            // Compare against the remaining balance (canonical outstanding) so a
            // balance payment after an advance isn't flagged against the full total.
            const balanceDue = (canonicalOutstanding && canonicalOutstanding.outstanding_amount != null)
              ? Number(canonicalOutstanding.outstanding_amount)
              : invoiceTotal;
            const received = parseFloat(paymentForm.amount || 0);
            const fee = parseFloat(paymentForm.hrdcorp_service_fee || 0);
            let cnAmount = 0;
            if (paymentForm.create_cn) {
              cnAmount = paymentForm.cn_mode === 'amount'
                ? parseFloat(paymentForm.cn_amount || 0)
                : (invoiceTotal * parseFloat(paymentForm.cn_percentage || 0) / 100);
            }
            const sum = received + fee + cnAmount;
            const shortfall = Math.round((balanceDue - received - fee) * 100) / 100;
            const matches = Math.abs(sum - balanceDue) < 0.01;
            return (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3" data-testid="hrdcorp-fields">
                <h4 className="text-sm font-semibold text-blue-800">HRDCorp Grant Payment Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Invoice Amount (RM)</Label>
                    <Input value={invoiceTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })} disabled className="bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs">HRDCorp Service Fee (RM) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentForm.hrdcorp_service_fee}
                      onChange={(e) => setPaymentForm({ ...paymentForm, hrdcorp_service_fee: e.target.value })}
                      placeholder="e.g. 400.00"
                      data-testid="hrdcorp-service-fee-input"
                    />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="hrdcorp-partial-toggle">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!paymentForm.is_partial_payment}
                    onChange={(e) => setPaymentForm({ ...paymentForm, is_partial_payment: e.target.checked })}
                  />
                  <span className="text-blue-900">
                    <strong>This is an upfront advance / partial payment</strong> (e.g. HRDCorp 30% upfront).
                    The remaining balance stays outstanding on the invoice — no credit note needed.
                  </span>
                </label>
                {paymentForm.is_partial_payment ? (
                  <div className="text-xs p-2 rounded bg-blue-100 text-blue-800" data-testid="hrdcorp-partial-summary">
                    Advance: Received RM {received.toLocaleString('en-MY', { minimumFractionDigits: 2 })} + Fee RM {fee.toLocaleString('en-MY', { minimumFractionDigits: 2 })} = RM {(received + fee).toLocaleString('en-MY', { minimumFractionDigits: 2 })}.
                    {canonicalOutstanding && (
                      <> Balance remaining after this: <strong>RM {Math.max(0, Number(canonicalOutstanding.outstanding_amount || 0) - received - fee).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</strong>.</>
                    )}
                  </div>
                ) : (
                <div className={`text-xs p-2 rounded ${matches ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                  Received RM {received.toLocaleString('en-MY', { minimumFractionDigits: 2 })} + Fee RM {fee.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  {cnAmount > 0 && (<> + CN RM {cnAmount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</>)}
                  {' = '}RM {sum.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  {invoiceTotal > 0 && (matches ? ' ✓ matches balance due' : ` ✗ must equal balance due RM ${balanceDue.toLocaleString('en-MY', { minimumFractionDigits: 2 })}`)}
                </div>
                )}
                {!paymentForm.is_partial_payment && shortfall > 0.01 && !paymentForm.create_cn && (
                  <div className="text-xs p-2 rounded bg-purple-50 border border-purple-200 text-purple-800">
                    <strong>Partial grant detected.</strong> HRDCorp is covering RM {(received + fee).toLocaleString('en-MY', { minimumFractionDigits: 2 })} of RM {balanceDue.toLocaleString('en-MY', { minimumFractionDigits: 2 })} due.
                    If this is an <strong>upfront advance</strong>, tick the box above. If HRDCorp paid less and the rest is written off, tick &ldquo;Create Credit Note&rdquo; below for the RM {shortfall.toLocaleString('en-MY', { minimumFractionDigits: 2 })} shortfall.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">HRDCorp Invoice Number *</Label>
                    <Input
                      value={paymentForm.hrdcorp_invoice_number}
                      onChange={(e) => setPaymentForm({ ...paymentForm, hrdcorp_invoice_number: e.target.value })}
                      placeholder="e.g. HRD-INV-2026-001"
                      data-testid="hrdcorp-invoice-number-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">HRDCorp Invoice Date</Label>
                    <Input
                      type="date"
                      value={paymentForm.hrdcorp_invoice_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, hrdcorp_invoice_date: e.target.value })}
                      data-testid="hrdcorp-invoice-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">HRDCorp Invoice Attachment</Label>
                  {!paymentForm.hrdcorp_invoice_url ? (
                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleHrdInvoiceFileChange}
                      className="bg-white"
                      data-testid="hrdcorp-invoice-file-input"
                    />
                  ) : (
                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                      <span className="text-xs text-gray-700 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-blue-600" /> HRDCorp invoice attached
                      </span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setPaymentForm({ ...paymentForm, hrdcorp_invoice_url: '' })}>
                        <X className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Proof of Payment Upload (Optional) */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2" data-testid="proof-of-payment-section">
            <Label className="text-blue-800 font-medium flex items-center gap-2">
              <Paperclip className="w-4 h-4" /> Proof of Payment (Optional)
            </Label>
            {!paymentForm.receipt_url ? (
              <Input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleProofFileChange}
                data-testid="proof-of-payment-input"
                className="bg-white"
              />
            ) : (
              <div className="flex items-center justify-between p-2 bg-white rounded border" data-testid="proof-of-payment-preview">
                <div className="flex items-center gap-2 text-sm text-gray-700 truncate">
                  <ImageIcon className="w-4 h-4 text-blue-600 shrink-0" />
                  {paymentForm.receipt_url.startsWith('data:application/pdf')
                    ? <span>PDF attached</span>
                    : <img src={paymentForm.receipt_url} alt="proof" className="h-12 w-12 object-cover rounded border" />}
                  <span className="text-xs text-gray-500">Attached</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentForm({ ...paymentForm, receipt_url: '' })}
                  data-testid="remove-proof-btn"
                >
                  <X className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            )}
            <p className="text-xs text-blue-700">Upload bank transfer receipt, cheque image, or any proof (image or PDF, max 5MB).</p>
          </div>
          
          {/* Credit Note Option — always available. For HRDCorp partial grants, use this to write off the shortfall. */}
          {(
          <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-3">
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="create-cn"
                checked={paymentForm.create_cn}
                onChange={(e) => setPaymentForm({...paymentForm, create_cn: e.target.checked})}
              />
              <Label htmlFor="create-cn" className="text-red-700 font-medium">
                Create Credit Note (e.g., HRDCorp deduction)
              </Label>
            </div>
            {paymentForm.create_cn && (() => {
              const selectedInvoice = invoices.find(inv => inv.id === paymentForm.invoice_id);
              const invoiceTotal = selectedInvoice?.total_amount || 0;
              const calcAmount = paymentForm.cn_mode === 'percentage' 
                ? ((invoiceTotal * parseFloat(paymentForm.cn_percentage || 0)) / 100).toFixed(2)
                : parseFloat(paymentForm.cn_amount || 0).toFixed(2);
              const calcPct = paymentForm.cn_mode === 'amount' && invoiceTotal > 0
                ? ((parseFloat(paymentForm.cn_amount || 0) / invoiceTotal) * 100).toFixed(2)
                : paymentForm.cn_percentage;
              
              return (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button 
                      type="button" size="sm" variant={paymentForm.cn_mode === 'percentage' ? 'default' : 'outline'}
                      onClick={() => setPaymentForm({...paymentForm, cn_mode: 'percentage'})}
                      className="text-xs"
                    >By Percentage (%)</Button>
                    <Button 
                      type="button" size="sm" variant={paymentForm.cn_mode === 'amount' ? 'default' : 'outline'}
                      onClick={() => setPaymentForm({...paymentForm, cn_mode: 'amount'})}
                      className="text-xs"
                    >By Amount (RM)</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {paymentForm.cn_mode === 'percentage' ? (
                      <div>
                        <Label className="text-sm">Deduction %</Label>
                        <Input 
                          type="number" 
                          value={paymentForm.cn_percentage}
                          onChange={(e) => setPaymentForm({...paymentForm, cn_percentage: e.target.value})}
                          placeholder="4"
                        />
                        {invoiceTotal > 0 && <p className="text-xs text-gray-500 mt-1">= RM {calcAmount}</p>}
                      </div>
                    ) : (
                      <div>
                        <Label className="text-sm">Deduction Amount (RM)</Label>
                        <Input 
                          type="number" 
                          value={paymentForm.cn_amount}
                          onChange={(e) => setPaymentForm({...paymentForm, cn_amount: e.target.value})}
                          placeholder="300"
                        />
                        {invoiceTotal > 0 && <p className="text-xs text-gray-500 mt-1">= {calcPct}% of RM {invoiceTotal.toLocaleString()}</p>}
                      </div>
                    )}
                    <div>
                      <Label className="text-sm">Reason</Label>
                      <Input 
                        value={paymentForm.cn_reason}
                        onChange={(e) => setPaymentForm({...paymentForm, cn_reason: e.target.value})}
                        placeholder="HRDCorp Levy"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          )}
          
          <div className="flex gap-2">
            <Button
              onClick={handleRecordPayment}
              disabled={paymentDisabled}
              className="bg-green-600 hover:bg-green-700 flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="record-payment-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
            <Button variant="outline" onClick={clearForm} data-testid="clear-payment-form-btn">
              Clear
            </Button>
          </div>
          {outstandingError && (
            <p className="text-xs text-red-600 mt-2" data-testid="outstanding-error-hint">
              {outstandingError}
              <Button variant="ghost" size="sm" className="ml-1 h-auto p-1 text-xs" onClick={() => handleInvoiceSelect(paymentForm.invoice_id)}>
                Retry
              </Button>
            </p>
          )}
          {outstandingLoading && (
            <p className="text-xs text-slate-500 mt-2" data-testid="outstanding-loading-hint">Verifying canonical outstanding…</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Payments */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <CardTitle>Recent Payments</CardTitle>
            <div className="flex gap-2">
              {onViewAllPayments && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onViewAllPayments}
                  data-testid="view-all-payments-btn"
                >
                  <History className="w-4 h-4 mr-1" />
                  View All Payments
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onRefresh}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No payments recorded yet</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={onRefresh}>
                Load Payments
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.slice(0, 10).map((payment) => (
                <div key={payment.id} className={`p-3 rounded-lg ${payment.status === 'reversed' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{payment.invoice_number}</p>
                        {payment.status === 'reversed' && (
                          <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded" data-testid="reversed-badge">REVERSED</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{payment.payment_date}</p>
                      {payment.status === 'reversed' && payment.reversal_reason && (
                        <p className="text-xs text-red-600 mt-1">Reason: {payment.reversal_reason}</p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className={`font-bold ${payment.status === 'reversed' ? 'text-red-400 line-through' : 'text-green-600'}`}>RM {payment.amount?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{payment.payment_method}</p>
                      </div>
                      {payment.status !== 'reversed' && (
                        <>
                          {payment.payment_type === 'hrdcorp' && (
                            <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded" data-testid={`hrdcorp-badge-${payment.id}`}>HRDCORP</span>
                          )}
                          {payment.has_hrdcorp_invoice && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewHrdInvoice(payment.id)}
                              title="View HRDCorp Invoice"
                              data-testid={`view-hrd-invoice-btn-${payment.id}`}
                            >
                              <Paperclip className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                          {payment.has_receipt && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewProof(payment.id)}
                              title="View Proof of Payment"
                              data-testid={`view-proof-btn-${payment.id}`}
                            >
                              <Paperclip className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handlePrintReceipt(payment)}
                            title="Print Receipt"
                          >
                            <Receipt className="w-4 h-4 text-purple-600" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { PaymentsTab };
